import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../lib/utils';

export interface CopyBlockProps extends React.ComponentPropsWithoutRef<'div'> {
  value: string;
  label?: string;
  truncate?: boolean;
}

export function CopyBlock({
  value,
  label,
  truncate = true,
  className,
  ...props
}: CopyBlockProps) {
  const [copied, setCopied] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }

  const shouldTruncate = truncate && value.length > 120 && !expanded;

  return (
    <div className={cn('space-y-1.5', className)} {...props}>
      {label ? (
        <p className="text-xs font-medium text-muted-foreground">
          {label}
        </p>
      ) : null}
      <div className="relative rounded-md border border-border bg-subtle p-3 pr-10">
        <pre
          className={cn(
            'font-mono text-xs text-foreground whitespace-pre-wrap break-all',
            shouldTruncate && 'line-clamp-2'
          )}
        >
          {value}
        </pre>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="absolute right-2 top-2 grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-success" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
        {truncate && value.length > 120 ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1.5 text-xs text-accent hover:underline"
          >
            {expanded ? 'Collapse' : 'Show full value'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
